import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { jsonFetch } from '@/lib/api/jsonFetch'
import type {
  CreateRequestInput,
  LabelsResponse,
  SampleProduct,
  SampleRep,
  SampleRequestDetail,
  SampleRequestListResponse,
  SampleRequestStats,
  SampleRequestSummary,
  Stage,
  TransitionResponse,
} from './types'

const KEY = ['sample-requests'] as const

export function useSampleProducts() {
  return useQuery({
    queryKey: [...KEY, 'products'],
    queryFn: () =>
      jsonFetch<{ products: SampleProduct[] }>('/api/sample-requests/products'),
    staleTime: 5 * 60_000, // product catalog rarely changes
  })
}

export function useSampleReps() {
  return useQuery({
    queryKey: [...KEY, 'reps'],
    queryFn: () =>
      jsonFetch<{ reps: SampleRep[] }>('/api/sample-requests/reps'),
    staleTime: 5 * 60_000,
  })
}

export interface SampleRequestFilters {
  status?: Stage
  q?: string
  assigned_to?: string
  archived?: boolean
  overdue_only?: boolean
  limit?: number
  offset?: number
}

export function useSampleRequestList(filters: SampleRequestFilters = {}) {
  const params = new URLSearchParams()
  if (filters.status) params.set('status', filters.status)
  if (filters.q) params.set('q', filters.q)
  if (filters.assigned_to) params.set('assigned_to', filters.assigned_to)
  if (filters.archived) params.set('archived', 'true')
  if (filters.overdue_only) params.set('overdue_only', 'true')
  params.set('limit', String(filters.limit ?? 200))
  params.set('offset', String(filters.offset ?? 0))
  const qs = params.toString()

  return useQuery({
    queryKey: [...KEY, 'list', filters],
    queryFn: () =>
      jsonFetch<SampleRequestListResponse>(`/api/sample-requests/?${qs}`),
    staleTime: 15_000,
    refetchInterval: 30_000,
  })
}

export function useSampleRequestStats() {
  return useQuery({
    queryKey: [...KEY, 'stats'],
    queryFn: () => jsonFetch<SampleRequestStats>('/api/sample-requests/stats'),
    staleTime: 15_000,
    refetchInterval: 60_000,
  })
}

export function useSampleRequestDetail(requestId: string | null) {
  return useQuery({
    queryKey: [...KEY, 'detail', requestId],
    queryFn: () =>
      jsonFetch<SampleRequestDetail>(`/api/sample-requests/${requestId}`),
    enabled: !!requestId,
    staleTime: 10_000,
  })
}

export function useCreateSampleRequest() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: CreateRequestInput) =>
      jsonFetch<SampleRequestSummary>('/api/sample-requests/', {
        method: 'POST',
        body: input,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY })
    },
  })
}

export function useTransitionSampleRequest() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      requestId,
      targetStage,
      note,
    }: {
      requestId: string
      targetStage: Stage
      note?: string
    }) =>
      jsonFetch<TransitionResponse>(
        `/api/sample-requests/${requestId}/transition`,
        {
          method: 'POST',
          body: { target_stage: targetStage, note },
        },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY })
    },
  })
}

export function usePatchSampleRequest() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      requestId,
      edits,
    }: {
      requestId: string
      edits: Record<string, unknown>
    }) =>
      jsonFetch<SampleRequestSummary>(`/api/sample-requests/${requestId}`, {
        method: 'PATCH',
        body: edits,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY })
    },
  })
}

export interface Party {
  kind: 'company' | 'contact'
  id: number
  name: string
  company_id: number | null
  company_name: string | null
  email: string | null
}

/** Unified company-or-contact search for the New Sample Request form. */
export async function searchParties(q: string): Promise<Party[]> {
  if (!q.trim()) return []
  const res = await jsonFetch<{ parties: Party[] }>(
    `/api/sample-requests/search-parties?q=${encodeURIComponent(q)}`,
  )
  return res.parties ?? []
}

export interface ContactDetail {
  address: string
  company_id: number | null
  company_name: string | null
}

/** Fetch a contact's company (commercial parent) + formatted Odoo address. */
export async function fetchContactDetail(partnerId: number): Promise<ContactDetail> {
  const res = await jsonFetch<ContactDetail>(
    `/api/sample-requests/contact/${partnerId}/address`,
  )
  return {
    address: res.address ?? '',
    company_id: res.company_id ?? null,
    company_name: res.company_name ?? null,
  }
}

/** Upload a shipping label to a sample request (multipart). */
export function useUploadSampleLabel() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ requestId, file }: { requestId: string; file: File }) => {
      const fd = new FormData()
      fd.append('file', file)
      return jsonFetch<LabelsResponse>(
        `/api/sample-requests/${requestId}/labels`,
        { method: 'POST', rawBody: fd },
      )
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY })
    },
  })
}

/** Detach a shipping label from a sample request. */
export function useDeleteSampleLabel() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      requestId,
      documentId,
    }: {
      requestId: string
      documentId: number
    }) =>
      jsonFetch<LabelsResponse>(
        `/api/sample-requests/${requestId}/labels/${documentId}`,
        { method: 'DELETE' },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY })
    },
  })
}

export function useLinkOdooDelivery() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      requestId,
      pickingReference,
    }: {
      requestId: string
      pickingReference: string
    }) =>
      jsonFetch(`/api/sample-requests/${requestId}/link-odoo`, {
        method: 'POST',
        body: { picking_reference: pickingReference },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY })
    },
  })
}
